import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, Stack, TextField, Typography} from 'views/components';
import {AccessTime as AccessTimeIcon, Add as AddIcon, ArrowForward as ArrowForwardIcon} from 'views/icons';
import {Translate, translate} from 'views/module';

import {fieldSx} from './AgentJobDraftFields';
import {
  buildPayload,
  getActionLabel,
  getDraftBody,
  getDraftComponent,
  getDraftIcon,
  getDraftSubtitle,
  getDraftTitle,
} from '../agentJobTypes';
import {formatJson} from '../agentJobTypeUtils';

import moment from 'moment';

const nowExecutionDelayMs = 15 * 1000;

const formatDateTimeLocal = (value) => {
  const valueMoment = moment(value);
  return valueMoment.isValid() ? valueMoment.format('YYYY-MM-DDTHH:mm') : '';
};

const normalizeDateTimeLocal = (value) => {
  const valueMoment = moment(value);

  if (!value || !valueMoment.isValid()) {
    throw new Error(translate('case.Detail.AgentJobs.InvalidExecuteAt'));
  }

  return valueMoment.toISOString();
};

const getInitials = (value) => {
  return (value || '')
      .split(/\s+/)
      .map((part) => part[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || 'AI';
};

const getCountdownLabel = (executeAt, now) => {
  const executeAtTime = new Date(executeAt).getTime();

  if (Number.isNaN(executeAtTime)) {
    return '--';
  }

  const differenceMs = executeAtTime - now;

  if (differenceMs <= 0) {
    return translate('case.Detail.AgentJobs.Due');
  }

  const totalSeconds = Math.ceil(differenceMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) {
    return `${days}d ${hours}h`;
  }

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const getProgressWidth = (job, now) => {
  const createdAt = new Date(job.createdAt).getTime();
  const executeAt = new Date(job.executeAt).getTime();

  if (Number.isNaN(createdAt) || Number.isNaN(executeAt) || executeAt <= createdAt) {
    return '100%';
  }

  const progress = Math.min(1, Math.max(0, (now - createdAt) / (executeAt - createdAt)));
  return `${Math.max(5, progress * 100)}%`;
};

const JobDraftEditor = (props) => {
  const {
    bodyJsonText,
    draftPayload,
    job,
    paramsJsonText,
    payloadJsonText,
    setBodyJsonText,
    setDraftPayload,
    setParamsJsonText,
    setPayloadJsonText,
  } = props;
  const AgentJobTypeDraft = getDraftComponent(job.actionType);
  const updatePayloadField = (field) => (value) => {
    setDraftPayload((currentPayload) => ({
      ...currentPayload,
      [field]: value,
    }));
  };

  return (
    <AgentJobTypeDraft
      bodyJsonText={bodyJsonText}
      draftPayload={draftPayload}
      paramsJsonText={paramsJsonText}
      payloadJsonText={payloadJsonText}
      setBodyJsonText={setBodyJsonText}
      setParamsJsonText={setParamsJsonText}
      setPayloadJsonText={setPayloadJsonText}
      updatePayloadField={updatePayloadField}
    />
  );
};

JobDraftEditor.propTypes = {
  bodyJsonText: PropTypes.string,
  draftPayload: PropTypes.object.isRequired,
  job: PropTypes.object.isRequired,
  paramsJsonText: PropTypes.string,
  payloadJsonText: PropTypes.string,
  setBodyJsonText: PropTypes.func.isRequired,
  setDraftPayload: PropTypes.func.isRequired,
  setParamsJsonText: PropTypes.func.isRequired,
  setPayloadJsonText: PropTypes.func.isRequired,
};

const AgentJobDraftCard = (props) => {
  const {
    job,
    onCancelJob,
    onSaveJob,
    onSendJobNow,
    participantDetails,
  } = props;
  const [now, setNow] = React.useState(Date.now());
  const [isEditing, setIsEditing] = React.useState(false);

  React.useEffect(() => {
    const interval = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => window.clearInterval(interval);
  }, []);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isDiscarding, setIsDiscarding] = React.useState(false);
  const [isSendingNow, setIsSendingNow] = React.useState(false);
  const [draftPayload, setDraftPayload] = React.useState(job.actionPayload || {});
  const [executeAtValue, setExecuteAtValue] = React.useState(formatDateTimeLocal(job.executeAt));
  const [payloadJsonText, setPayloadJsonText] = React.useState(formatJson(job.actionPayload || {}));
  const [paramsJsonText, setParamsJsonText] = React.useState(formatJson(job.actionPayload?.params || {}));
  const [bodyJsonText, setBodyJsonText] = React.useState(formatJson(job.actionPayload?.body || {}));
  const [errorMessage, setErrorMessage] = React.useState('');

  React.useEffect(() => {
    if (isEditing) {
      return;
    }

    setDraftPayload(job.actionPayload || {});
    setExecuteAtValue(formatDateTimeLocal(job.executeAt));
    setPayloadJsonText(formatJson(job.actionPayload || {}));
    setParamsJsonText(formatJson(job.actionPayload?.params || {}));
    setBodyJsonText(formatJson(job.actionPayload?.body || {}));
    setErrorMessage('');
  }, [job, isEditing]);

  const title = getDraftTitle(job, participantDetails);
  const subtitle = getDraftSubtitle(job, participantDetails);
  const actionLabel = getActionLabel(job);
  const DraftIcon = getDraftIcon(job.actionType);
  const body = getDraftBody(job);
  const countdownLabel = getCountdownLabel(job.executeAt, now);
  const progressWidth = getProgressWidth(job, now);

  const buildPayloadForSave = () => {
    return buildPayload(job, {
      bodyJsonText,
      draftPayload,
      paramsJsonText,
      payloadJsonText,
    });
  };

  const handleSave = async () => {
    setErrorMessage('');
    setIsSaving(true);

    try {
      const nextPayload = buildPayloadForSave();
      const executeAtChanged = executeAtValue !== formatDateTimeLocal(job.executeAt);
      const nextExecuteAt = executeAtChanged ? normalizeDateTimeLocal(executeAtValue) : job.executeAt;
      const updatedJob = await onSaveJob(job, nextPayload, nextExecuteAt);

      if (updatedJob) {
        setIsEditing(false);
      }
    } catch (error) {
      setErrorMessage(error.message || translate('case.Detail.AgentJobs.SaveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = async () => {
    setErrorMessage('');
    setIsDiscarding(true);

    try {
      await onCancelJob(job);
    } catch (error) {
      setErrorMessage(error.message || translate('case.Detail.AgentJobs.DiscardFailed'));
    } finally {
      setIsDiscarding(false);
    }
  };

  const handleSendNow = async () => {
    setErrorMessage('');
    setIsSendingNow(true);

    try {
      await onSendJobNow(job, new Date(Date.now() + nowExecutionDelayMs).toISOString());
    } catch (error) {
      setErrorMessage(error.message || translate('case.Detail.AgentJobs.SendNowFailed'));
    } finally {
      setIsSendingNow(false);
    }
  };

  const primaryDisabled = isSaving || isDiscarding || isSendingNow;

  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #F4CDBD',
        borderRadius: '8px',
        boxShadow: '0px 10px 24px rgba(91, 47, 28, 0.13)',
        overflow: 'hidden',
      }}
    >
      <Stack
        sx={{
          px: {
            xs: 2,
            sm: 2.5,
          },
          py: 2,
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Stack direction='row' alignItems='center' sx={{gap: 1}}>
            <Box
              sx={{
                width: '18px',
                height: '18px',
                borderRadius: '5px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#E4875B',
                color: '#FFFFFF',
              }}
            >
              <AddIcon sx={{fontSize: '13px'}} />
            </Box>
            <Typography
              sx={{
                fontSize: '10px',
                fontWeight: 800,
                letterSpacing: 0,
                textTransform: 'uppercase',
                color: '#172033',
              }}
            >
              <Translate expression='case.Detail.AgentJobs.DraftTitle' /> - <Translate expression='case.Detail.AgentJobs.HoldingForReview' />
            </Typography>
          </Stack>
          <Stack
            direction='row'
            alignItems='center'
            sx={{
              minWidth: '45px',
              px: 0.75,
              py: 0.35,
              gap: 0.4,
              borderRadius: '999px',
              backgroundColor: '#FBEEE9',
              color: '#D66F45',
            }}
          >
            <AccessTimeIcon sx={{fontSize: '11px'}} />
            <Typography
              sx={{
                fontSize: '10px',
                fontWeight: 700,
                lineHeight: '12px',
              }}
            >
              {countdownLabel}
            </Typography>
          </Stack>
        </Box>
        <Stack direction='row' alignItems='center' sx={{gap: 1}}>
          <Box
            sx={{
              width: '22px',
              height: '22px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              backgroundColor: DraftIcon ? '#FFFFFF' : '#31B65D',
              border: DraftIcon ? '1px solid #E4875B' : 'none',
              color: DraftIcon ? '#E4875B' : '#FFFFFF',
              fontSize: '9px',
              fontWeight: 800,
            }}
          >
            {
              DraftIcon ? (
                <DraftIcon sx={{fontSize: '15px'}} />
              ) : (
                getInitials(title.replace(/^To\s+/i, ''))
              )
            }
          </Box>
          <Box sx={{minWidth: 0}}>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 800,
                lineHeight: '15px',
                color: '#071126',
                wordBreak: 'break-word',
              }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                fontSize: '10px',
                fontWeight: 500,
                lineHeight: '13px',
                color: '#788091',
                wordBreak: 'break-word',
              }}
            >
              {subtitle || actionLabel}
            </Typography>
          </Box>
        </Stack>
        {
          isEditing ? (
            <Stack sx={{gap: 1.25}}>
              <JobDraftEditor
                bodyJsonText={bodyJsonText}
                draftPayload={draftPayload}
                job={job}
                paramsJsonText={paramsJsonText}
                payloadJsonText={payloadJsonText}
                setBodyJsonText={setBodyJsonText}
                setDraftPayload={setDraftPayload}
                setParamsJsonText={setParamsJsonText}
                setPayloadJsonText={setPayloadJsonText}
              />
              <TextField
                fullWidth
                label={translate('case.Detail.AgentJobs.Fields.ExecuteAt')}
                onChange={setExecuteAtValue}
                size='small'
                type='datetime-local'
                value={executeAtValue}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={fieldSx}
              />
            </Stack>
          ) : (
            <Box
              sx={{
                backgroundColor: '#FFF3EF',
                border: '1px solid #F7E2DA',
                borderRadius: '6px',
                px: 1.5,
                py: 1.25,
              }}
            >
              <Typography
                sx={{
                  fontSize: '12px',
                  lineHeight: '18px',
                  color: '#071126',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {body || translate('case.Detail.AgentJobs.EmptyPayload')}
              </Typography>
            </Box>
          )
        }
        <Box
          sx={{
            height: '3px',
            backgroundColor: '#EFEFEF',
            borderRadius: '999px',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: progressWidth,
              height: '100%',
              backgroundColor: '#E4875B',
              borderRadius: '999px',
              transition: 'width 250ms ease',
            }}
          />
        </Box>
        {
          errorMessage && (
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#D34A2C',
              }}
            >
              {errorMessage}
            </Typography>
          )
        }
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: {
              xs: 'stretch',
              sm: 'center',
            },
            gap: 1,
            flexDirection: {
              xs: 'column',
              sm: 'row',
            },
          }}
        >
          <Stack
            direction='row'
            sx={{
              gap: 1,
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
            }}
          >
            {
              isEditing ? (
                <>
                  <Button
                    disabled={primaryDisabled}
                    onClick={() => setIsEditing(false)}
                    variant='outlined'
                    sx={{
                      height: '28px',
                      px: 1.5,
                      borderColor: '#E1E2E8',
                      borderRadius: '7px',
                      color: '#071126',
                      fontSize: '11px',
                      fontWeight: 700,
                      textTransform: 'none',
                      backgroundColor: '#FFFFFF',
                      boxShadow: 'none',
                    }}
                  >
                    <Translate expression='case.Detail.AgentJobs.Cancel' />
                  </Button>
                  <Button
                    disabled={primaryDisabled}
                    onClick={handleSave}
                    disableElevation
                    sx={{
                      'height': '28px',
                      'px': 1.5,
                      'borderRadius': '7px',
                      'color': '#FFFFFF',
                      'fontSize': '11px',
                      'fontWeight': 800,
                      'textTransform': 'none',
                      'backgroundColor': '#E4875B',
                      'boxShadow': 'none',
                      '&:hover': {
                        backgroundColor: '#D9784B',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    <Translate expression='case.Detail.AgentJobs.Save' />
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    disabled={primaryDisabled}
                    onClick={() => setIsEditing(true)}
                    variant='outlined'
                    sx={{
                      height: '28px',
                      px: 1.5,
                      borderColor: '#E1E2E8',
                      borderRadius: '7px',
                      color: '#071126',
                      fontSize: '11px',
                      fontWeight: 700,
                      textTransform: 'none',
                      backgroundColor: '#FFFFFF',
                      boxShadow: 'none',
                    }}
                  >
                    <Translate expression='case.Detail.AgentJobs.Edit' />
                  </Button>
                  <Button
                    disabled={primaryDisabled}
                    onClick={handleDiscard}
                    variant='outlined'
                    sx={{
                      height: '28px',
                      px: 1.5,
                      borderColor: '#F2D8D1',
                      borderRadius: '7px',
                      color: '#E04F3F',
                      fontSize: '11px',
                      fontWeight: 700,
                      textTransform: 'none',
                      backgroundColor: '#FFFFFF',
                      boxShadow: 'none',
                    }}
                  >
                    <Translate expression='case.Detail.AgentJobs.Discard' />
                  </Button>
                  <Button
                    disabled={primaryDisabled}
                    onClick={handleSendNow}
                    endIcon={<ArrowForwardIcon sx={{fontSize: '13px !important'}} />}
                    disableElevation
                    sx={{
                      'height': '28px',
                      'px': 1.5,
                      'borderRadius': '7px',
                      'color': '#FFFFFF',
                      'fontSize': '11px',
                      'fontWeight': 800,
                      'textTransform': 'none',
                      'backgroundColor': '#E4875B',
                      'boxShadow': 'none',
                      '&:hover': {
                        backgroundColor: '#D9784B',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    <Translate expression='case.Detail.AgentJobs.SendNow' />
                  </Button>
                </>
              )
            }
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

AgentJobDraftCard.propTypes = {
  job: PropTypes.object.isRequired,
  onCancelJob: PropTypes.func.isRequired,
  onSaveJob: PropTypes.func.isRequired,
  onSendJobNow: PropTypes.func.isRequired,
  participantDetails: PropTypes.object.isRequired,
};

export default AgentJobDraftCard;
